import React from "react";
import { Box, Checkbox, IconButton, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { WaitingUserItem } from "../types";

interface WaitingUserRowProps {
  user: WaitingUserItem;
  approved: boolean;
  onApproveToggle: (next: boolean) => void;
  onArrowClick?: (user: WaitingUserItem) => void;
  disabled?: boolean;
}

const NeutralBadge: React.FC<{ label: string }> = ({ label }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        padding: "4px 10px",
        borderRadius: "8px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        fontSize: "12px",
        fontWeight: 600,
        color: theme.palette.text.primary,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
};

const WaitingUserRow: React.FC<WaitingUserRowProps> = ({
  user,
  approved,
  onApproveToggle,
  onArrowClick,
  disabled,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("users-and-groups");
  const fullName =
    [user.userName, user.userSurname].filter(Boolean).join(" ").trim() || "-";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: "14px 16px",
        borderRadius: "10px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        [theme.breakpoints.down("lg")]: {
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1.5,
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 700,
            color: theme.palette.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {fullName}
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.text.tertiary,
            lineHeight: 1.4,
          }}
        >
          {`ID: ${user.userId ?? "-"}${
            user.userEmail ? ` | ${user.userEmail}` : ""
          }`}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          <Checkbox
            size="small"
            checked={approved}
            disabled={disabled}
            onChange={(_, next) => onApproveToggle(next)}
            sx={{ p: 0 }}
          />
          <Typography
            sx={{
              fontSize: "12px",
              color: theme.palette.text.tertiary,
            }}
          >
            {t("row.approve-auto-enrollment")}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
          [theme.breakpoints.down("lg")]: {
            justifyContent: "flex-start",
            flexWrap: "wrap",
          },
        }}
      >
        <NeutralBadge label={String(t("row.approval-required-badge"))} />

        <IconButton
          size="small"
          onClick={() => onArrowClick?.(user)}
          sx={{ color: theme.palette.primary.main }}
          aria-label="open detail"
        >
          <ArrowRight size={18} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default WaitingUserRow;
